// searchCommit — what text a search COMMIT should carry, as a pure rule with a name.
//
// 🔑 WHY THIS IS A MODULE AND NOT A TERNARY. It was one line inside a 15,000-line component:
//
//     const nq = (toks.length ? c.residual : src).trim()
//
// On 2026-09-04 I changed it to stop re-searching words that had become chips — the owner's
// rule, and a correct rule — and shipped a search that committed an EMPTY query, because the
// branch I edited is also the one that carries street and place suggestions whose entire
// mechanism is the text. There was no test that could fail, because there was nothing importable
// to test: the rule lived where only a browser could reach it.
//
// So the repair is not "be more careful with that ternary". It is: give the rule a name, a
// signature, and an invariant that a change cannot violate silently.
//
// 🔒 THE INVARIANT: a commit must never produce NOTHING. If the caller applies no predicate of
// its own, the commit must carry text — the raw phrase if the residual is empty. A search that
// holds neither a filter nor a query returns the unfiltered pool while the agent believes they
// searched, which is the worst outcome this surface can produce.

import { addDistrictIncludes, addDistrictExcludes } from './districtFilter.js'

/**
 * @param {object}   a
 * @param {string}   a.raw          what the agent typed
 * @param {string}   a.residual     the compiler's leftover after recognised tokens were claimed
 * @param {number}   a.liveTokens   recognised tokens that were NOT dismissed
 * @param {boolean}  a.callerHasPredicate
 *        TRUE when the caller has already applied a real filter for this commit (a street id, a
 *        complex id). Only then may the text be dropped entirely — the chip IS the search, and
 *        re-sending the phrase would AND a weaker duplicate predicate onto an exact one.
 * @returns {string} the text to commit
 */
export function committedTextFor ({ raw = '', residual = '', liveTokens = 0, callerHasPredicate = false } = {}) {
  const r = String(residual || '').trim()
  const src = String(raw || '').trim()

  // A predicate already exists: the residual is the only text worth sending, and empty is a
  // legitimate answer. This is the owner's rule — "never look for wording if words were resolved
  // to chips" — and it is safe HERE precisely because a predicate is known to exist.
  if (callerHasPredicate) return r

  // Tokens survived compilation, so the residual is what the recognisers could not claim.
  if (liveTokens > 0) return r

  // Nothing was recognised and no predicate was applied. The raw phrase is the ONLY thing that
  // can search, so it commits even if the compiler dismissed every token as noise — which is
  // exactly the street/place suggestion case: those dismiss the wrong-district tokens as noise
  // and rely on the text.
  // ⚠️ `|| r` is not defensive padding — the invariant test found it. A caller with an empty raw
  // but a surviving residual would otherwise commit nothing, and a rule that can return empty
  // for ANY input is one edit away from being the bug it replaced. Prefer the raw phrase, but
  // never return nothing while text exists.
  return src || r
}

/**
 * The safety net, stated separately so it can be asserted on its own.
 * A commit is valid when it carries text OR the caller applied a predicate. Neither = a search
 * that silently returns everything.
 */
export function commitIsMeaningful ({ text = '', callerHasPredicate = false, liveTokens = 0 } = {}) {
  // ⚠️ liveTokens counts as a predicate, and leaving it out was a MODELLING error the invariant
  // test caught: a recognised token is folded into advFilters by the commit itself, so
  // "rooms:2 with nothing left over" carries a real filter even though its text is empty and no
  // caller applied anything. Without this the invariant calls the most ordinary search on the
  // surface meaningless.
  return Boolean(String(text || '').trim()) || Boolean(callerHasPredicate) || liveTokens > 0
}

/**
 * F-102 (2026-09-10): ENTER MAY ACCEPT AN UNAMBIGUOUS STREET SUGGESTION.
 * Measured before building (F-100): 66 of 601 logged residuals named a street, and the suggest endpoint
 * already answers them in context ("ნინო ჟვანიას" → exactly one street) — the agent pressed Enter instead
 * of clicking, and the name went to the server as fuzzy text. This decides, from what the agent could
 * already SEE, whether Enter should apply the street chip instead.
 * The rule is deliberately narrow, because a wrong street applied silently is worse than text:
 *   - a real Enter only (never a URL restore, never a suggestion click, never a hint);
 *   - nothing else compiled (`liveTokens === 0`) — with other chips in play, accepting a street would
 *     clear the box and lose them; that case keeps today's behaviour;
 *   - the suggestion is FOR THIS TEXT (the debounced answer for an older prefix must not decide);
 *   - exactly ONE street came back, with an id. Two or more stay text: the dropdown is still there to
 *     click, and "ჭავჭავაძის" alone matches three real streets.
 * @returns the street row to accept, or null
 */
export function streetToAcceptOnEnter ({ source = 'enter', residual = '', liveTokens = 0, suggest = null, typed = '', callerHasPredicate = false } = {}) {
  if (source !== 'enter' || callerHasPredicate || liveTokens > 0) return null
  const r = String(residual || '').trim()
  if (r.length < 3) return null
  if (!suggest || String(suggest.forQ || '').trim() !== String(typed || '').trim()) return null
  const streets = Array.isArray(suggest.streets) ? suggest.streets.filter(s => s && s.id) : []
  if (streets.length !== 1) return null
  // ⚠️ Desk filters audit 2026-10-28 DF01: a street echoed WITH house_number answers a numbered address
  // the street filter cannot keep. Accepting it on Enter cleared the box and searched the whole street,
  // silently dropping the number. Enter keeps the text; the row (which says "whole street") is a click away.
  if (streets[0].house_number) return null
  return streets[0]
}

// ── THE TOKEN OVERLAY (moved here from IntakeTab, 2026-10-22, so it can be tested) ─────────────────────
// The compiled tokens' patches merged over a filters object. Two callers, two modes:
//   · the live DRAFT (fold false) — what the box is expressing over the committed filters. Arrays union; a scalar
//     applies only where the COMMITTED side is unset, so a panel edit made while a token is live keeps winning.
//   · a COMMIT FOLD (fold true) — Enter, a URL restore, a refresh. The agent has just asked for it, so a token's scalar
//     REPLACES the committed one (2026-10-22: after "≤ 200,000 USD", typing "1000 ლარი" kept the 200,000 and only
//     switched the currency — "≤ 200,000 GEL").
// Price and currency are one pair in both modes: the currency rides only with a price it applies to, and a fold that
// changes the currency clears the committed bound this commit does not restate (a USD minimum is not a GEL one).
// Within one call the LAST token for a field wins, the same as compileQuery's `sentence` ("between 100k and 150k").
// `defaults` / `arrayKeys` are IntakeTab's ADV_DEFAULTS / ADV_ARRAY_KEYS.
const PRICE_KEYS = ['priceMin', 'priceMax']
export function applyTokenOverlay (f, tokens, { defaults = {}, arrayKeys = [], fold = false } = {}) {
  if (!tokens || !tokens.length) return f
  const n = { ...f }
  const unset = k => f[k] == null || f[k] === defaults[k]
  const foldPrice = new Set(tokens.flatMap(t => Object.keys(t.patch || {}).filter(k => PRICE_KEYS.includes(k))))
  for (const t of tokens) {
    const patch = t.patch || {}
    const priceKeys = Object.keys(patch).filter(k => PRICE_KEYS.includes(k))
    const priceApplies = fold || priceKeys.every(unset)
    for (const [k, v] of Object.entries(patch)) {
      // `restrictions` is a set of "<code>:<mode>" strings, so a plain set-union would let two INTENTS for the same
      // subject coexist (2026-07-28). One entry per subject: the incoming intent replaces the standing one.
      if (k === 'restrictions') {
        const incoming = new Set(v.map(s => String(s).slice(0, String(s).lastIndexOf(':'))))
        const kept = (n[k] || []).filter(s => !incoming.has(String(s).slice(0, String(s).lastIndexOf(':'))))
        n[k] = [...new Set([...kept, ...v])]
      }
      // A district ✓ also lifts the same code's ✕, and a typed ✕ ("არა ვაკე") lifts its ✓ — one code, one state.
      else if (k === 'districtCodes' || k === 'districtOwnCodes') Object.assign(n, addDistrictIncludes(n, k, v))
      else if (k === 'districtCodesNot' || k === 'districtOwnCodesNot') Object.assign(n, addDistrictExcludes(n, k, v))
      else if (arrayKeys.includes(k)) n[k] = [...new Set([...(n[k] || []), ...v])]
      else if (k === 'currency') {
        if (!priceKeys.length || !priceApplies) continue
        if (fold && v !== f.currency) for (const pk of PRICE_KEYS) if (!foldPrice.has(pk)) n[pk] = defaults[pk] ?? ''
        n[k] = v
      }
      else if (PRICE_KEYS.includes(k)) { if (priceApplies) n[k] = v }
      else if (fold || unset(k)) n[k] = v
    }
  }
  return n
}

// ── WHAT THE BOX SHOWS AFTER A FOLD (2026-10-22) ───────────────────────────────────────────────────────
// The text minus every span that folded into a filter, minus the spans the compiler understood but did not apply
// (`dropped`: "თბილისი" beside ვაკე, "ცენტრი" beside ბათუმი, a repeated word). Identity tokens (listing id, phone,
// cadastral — empty patch) keep their text: it IS their search. Dropped spans go only when something folded, and never
// when the token that made them redundant was dismissed. One rule for Enter, the URL restore and the refresh fold —
// the restore used to take the whole residual and so emptied an id/phone search.
export function boxTextAfterFold (text, tokens = [], dropped = []) {
  const src = String(text || '')
  const fold = (tokens || []).filter(tk => tk.start != null && Object.keys(tk.patch || {}).length)
  if (!fold.length) return src
  const spans = [...fold.map(tk => [tk.start, tk.end]), ...(dropped || []).map(d => [d.start, d.end])].sort((a, b) => a[0] - b[0])
  let out = '', pos = 0
  for (const [s, e] of spans) { if (s > pos) out += src.slice(pos, s); pos = Math.max(pos, e) }
  out += src.slice(pos)
  return out.replace(/\s{2,}/g, ' ').trim()
}
